import { useState, useEffect, useCallback } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useLLMTask } from "../hooks/useLLMTask";
import {
  Shield,
  Target,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Lightbulb,
  DollarSign,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  Loader2,
  RefreshCw,
  ChevronRight,
  Users,
  Zap,
  Award,
  Flag,
  Check,
  X,
  ExternalLink,
  Download,
  Share2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Battlecards() {
  const { darkMode, region, brand } = useOutletContext() || { darkMode: true, region: 'UAE', brand: 'blackswan' };
  const [battlecards, setBattlecards] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [selectedBattlecard, setSelectedBattlecard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  // Background task hook for heavy battlecard generation (Claude Sonnet
  // routinely exceeds the 55s inline ingress window with the rich SWOT
  // and objection-handler payloads).
  const battlecardTask = useLLMTask();

  const fetchData = useCallback(async () => {
    try {
      const [battlecardsRes, competitorsRes] = await Promise.all([
        axios.get(`${API}/advanced/battlecards/${brand}/${region}`),
        axios.get(`${API}/competitors?region=${region}&brand=${brand}`)
      ]);
      setBattlecards(battlecardsRes.data.battlecards || []);
      setCompetitors(competitorsRes.data || []);
    } catch (error) {
      console.error("Error fetching battlecards:", error);
    } finally {
      setLoading(false);
    }
  }, [brand, region]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const generateBattlecard = async (competitorId) => {
    setGenerating(competitorId);
    await battlecardTask.run({
      endpoint: `/advanced/battlecards/generate-async/${brand}/${region}/${competitorId}`,
      onDone: (result) => {
        fetchData();
        if (result?.battlecard) {
          setSelectedBattlecard(result.battlecard);
        }
        setGenerating(null);
      },
      onError: () => {
        setGenerating(null);
      },
    });
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'high': return 'text-red-500 bg-red-500/10';
      case 'medium': return 'text-amber-500 bg-amber-500/10';
      case 'low': return 'text-blue-500 bg-blue-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  const SWOTSection = ({ title, items, icon: Icon, color }) => (
    <div className={`rounded-xl p-4 ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-5 h-5 ${color}`} />
        <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h4>
        <Badge className="ml-auto border-0 bg-gray-500/20 text-gray-400">{items?.length || 0}</Badge>
      </div>
      <div className="space-y-2">
        {items?.map((item, idx) => (
          <div key={idx} className={`p-3 rounded-lg ${darkMode ? 'bg-[#161b22]' : 'bg-white'}`}>
            <div className="flex items-start justify-between">
              <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {item.title}
              </span>
              <Badge className={`text-xs border-0 ${getImpactColor(item.impact)}`}>
                {item.impact}
              </Badge>
            </div>
            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {item.description}
            </p>
          </div>
        ))}
        {(!items || items.length === 0) && (
          <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No data available</p>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#DC143C]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Competitive Battlecards
          </h1>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            AI-powered sales enablement cards for winning against competitors
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-[#DC143C]/10 text-[#DC143C] border-0">
            {battlecards.length} Battlecards
          </Badge>
          {battlecards.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.open(`${API}/export/battlecards/${brand}/${region}/csv`, '_blank')}
              className={`gap-2 ${darkMode ? 'border-[#30363d] text-gray-300 hover:bg-[#21262d]' : ''}`}
              data-testid="export-battlecards-csv"
            >
              <Download className="w-4 h-4" /> Export CSV
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Competitor List */}
        <div className="col-span-4">
          <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
            <CardHeader className="pb-3">
              <CardTitle className={`text-lg ${darkMode ? 'text-white' : ''}`}>Competitors</CardTitle>
              <CardDescription>Select a competitor to view or generate battlecard</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[600px] overflow-y-auto">
              {competitors.map((comp) => {
                const hasBattlecard = battlecards.find(b => b.competitor_id === comp.id);
                return (
                  <div
                    key={comp.id}
                    onClick={() => {
                      const bc = battlecards.find(b => b.competitor_id === comp.id);
                      if (bc) setSelectedBattlecard(bc);
                    }}
                    className={`p-3 rounded-lg cursor-pointer transition-all ${
                      selectedBattlecard?.competitor_id === comp.id
                        ? 'ring-2 ring-[#DC143C] bg-[#DC143C]/10'
                        : darkMode ? 'bg-[#21262d] hover:bg-[#30363d]' : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {comp.name}
                        </p>
                        <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {comp.website_url}
                        </p>
                      </div>
                      {hasBattlecard ? (
                        <Badge className="bg-emerald-500/10 text-emerald-500 border-0 text-xs">
                          <Check className="w-3 h-3 mr-1" />
                          Ready
                        </Badge>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            generateBattlecard(comp.id);
                          }}
                          disabled={generating === comp.id}
                          data-testid={`generate-battlecard-${comp.id}`}
                          className="h-7 text-xs"
                        >
                          {generating === comp.id ? (
                            <>
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              <span>{battlecardTask.elapsedSec}s</span>
                            </>
                          ) : (
                            <>
                              <Zap className="w-3 h-3 mr-1" />
                              Generate
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Battlecard Detail */}
        <div className="col-span-8">
          {selectedBattlecard ? (
            <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className={`text-xl ${darkMode ? 'text-white' : ''}`}>
                      {selectedBattlecard.competitor_name}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-2 mt-1">
                      <Badge className={`border-0 ${
                        selectedBattlecard.market_position === 'leader' ? 'bg-purple-500/10 text-purple-500' :
                        selectedBattlecard.market_position === 'challenger' ? 'bg-blue-500/10 text-blue-500' :
                        'bg-gray-500/10 text-gray-500'
                      }`}>
                        {selectedBattlecard.market_position || 'Unknown'}
                      </Badge>
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                        Confidence: {Math.round((selectedBattlecard.confidence_score || 0) * 100)}%
                      </span>
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => generateBattlecard(selectedBattlecard.competitor_id)}
                      disabled={generating === selectedBattlecard.competitor_id}
                    >
                      {generating === selectedBattlecard.competitor_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                    </Button>
                    <Button size="sm" variant="outline">
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className={`mb-4 ${darkMode ? 'bg-[#21262d]' : ''}`}>
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="swot">SWOT</TabsTrigger>
                    <TabsTrigger value="sales">Sales Plays</TabsTrigger>
                    <TabsTrigger value="winloss">Win/Loss</TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview" className="space-y-4">
                    <div className={`p-4 rounded-xl ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                      <h4 className={`font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Company Overview
                      </h4>
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {selectedBattlecard.company_overview || 'No overview available'}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className={`p-4 rounded-xl ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                        <h4 className={`font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          Target Market
                        </h4>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {selectedBattlecard.target_market || 'Not specified'}
                        </p>
                      </div>
                      <div className={`p-4 rounded-xl ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                        <h4 className={`font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          Value Proposition
                        </h4>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {selectedBattlecard.value_proposition || 'Not specified'}
                        </p>
                      </div>
                    </div>

                    {/* Pricing */}
                    {selectedBattlecard.pricing && (
                      <div className={`p-4 rounded-xl ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <DollarSign className="w-5 h-5 text-emerald-500" />
                          <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            Pricing Intelligence
                          </h4>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Model</p>
                            <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {selectedBattlecard.pricing.pricing_model || 'Unknown'}
                            </p>
                          </div>
                          <div>
                            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Range</p>
                            <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {selectedBattlecard.pricing.price_range || 'Unknown'}
                            </p>
                          </div>
                          <div>
                            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>vs Us</p>
                            <Badge className={`border-0 ${
                              selectedBattlecard.pricing.comparison_to_us === 'higher' ? 'bg-emerald-500/10 text-emerald-500' :
                              selectedBattlecard.pricing.comparison_to_us === 'lower' ? 'bg-red-500/10 text-red-500' :
                              'bg-gray-500/10 text-gray-500'
                            }`}>
                              {selectedBattlecard.pricing.comparison_to_us || 'Unknown'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Key Differentiators */}
                    {selectedBattlecard.differentiation?.length > 0 && (
                      <div className={`p-4 rounded-xl ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                        <h4 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          Key Differentiators
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedBattlecard.differentiation.map((diff) => (
                            <Badge key={diff} className="bg-purple-500/10 text-purple-500 border-0">
                              {diff}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="swot" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <SWOTSection
                        title="Strengths"
                        items={selectedBattlecard.strengths}
                        icon={ThumbsUp}
                        color="text-emerald-500"
                      />
                      <SWOTSection
                        title="Weaknesses"
                        items={selectedBattlecard.weaknesses}
                        icon={ThumbsDown}
                        color="text-red-500"
                      />
                      <SWOTSection
                        title="Opportunities"
                        items={selectedBattlecard.opportunities}
                        icon={Lightbulb}
                        color="text-amber-500"
                      />
                      <SWOTSection
                        title="Threats"
                        items={selectedBattlecard.threats}
                        icon={AlertTriangle}
                        color="text-purple-500"
                      />
                    </div>
                  </TabsContent>

                  <TabsContent value="sales" className="space-y-4">
                    {/* Talking Points */}
                    <div className={`p-4 rounded-xl ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                      <h4 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        <MessageSquare className="w-5 h-5 inline mr-2 text-blue-500" />
                        Talking Points
                      </h4>
                      <div className="space-y-3">
                        {selectedBattlecard.talking_points?.map((point, idx) => (
                          <div key={idx} className={`p-3 rounded-lg ${darkMode ? 'bg-[#161b22]' : 'bg-white'}`}>
                            <Badge className="mb-2 bg-blue-500/10 text-blue-500 border-0 text-xs">
                              {point.category}
                            </Badge>
                            <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {point.scenario}
                            </p>
                            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              → {point.response}
                            </p>
                          </div>
                        )) || <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No talking points available</p>}
                      </div>
                    </div>

                    {/* Objection Handlers */}
                    <div className={`p-4 rounded-xl ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                      <h4 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        <Shield className="w-5 h-5 inline mr-2 text-amber-500" />
                        Objection Handlers
                      </h4>
                      <div className="space-y-3">
                        {selectedBattlecard.objection_handlers?.map((obj, idx) => (
                          <div key={idx} className={`p-3 rounded-lg ${darkMode ? 'bg-[#161b22]' : 'bg-white'}`}>
                            <p className={`text-sm font-medium text-red-400`}>
                              ❓ {obj.objection}
                            </p>
                            <p className={`text-sm mt-1 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                              ✓ {obj.response}
                            </p>
                          </div>
                        )) || <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No objection handlers available</p>}
                      </div>
                    </div>

                    {/* Landmines */}
                    {selectedBattlecard.landmines?.length > 0 && (
                      <div className={`p-4 rounded-xl bg-red-500/10 border border-red-500/20`}>
                        <h4 className="font-semibold mb-3 text-red-500">
                          <Flag className="w-5 h-5 inline mr-2" />
                          Landmines (Avoid These Topics)
                        </h4>
                        <ul className="space-y-1">
                          {selectedBattlecard.landmines.map((mine) => (
                            <li key={mine} className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              ⚠️ {mine}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="winloss" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className={`p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20`}>
                        <h4 className="font-semibold mb-3 text-emerald-500">
                          <ThumbsUp className="w-5 h-5 inline mr-2" />
                          Why We Win
                        </h4>
                        <ul className="space-y-2">
                          {selectedBattlecard.why_we_win?.map((reason, idx) => (
                            <li key={idx} className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              ✓ {reason}
                            </li>
                          )) || <li className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No data available</li>}
                        </ul>
                      </div>
                      <div className={`p-4 rounded-xl bg-red-500/10 border border-red-500/20`}>
                        <h4 className="font-semibold mb-3 text-red-500">
                          <ThumbsDown className="w-5 h-5 inline mr-2" />
                          Why We Lose
                        </h4>
                        <ul className="space-y-2">
                          {selectedBattlecard.why_we_lose?.map((reason, idx) => (
                            <li key={idx} className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              ✗ {reason}
                            </li>
                          )) || <li className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No data available</li>}
                        </ul>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : (
            <Card className={`${darkMode ? 'bg-[#161b22] border-[#30363d]' : ''} h-full flex items-center justify-center`}>
              <CardContent className="text-center py-16">
                <Shield className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Select a Competitor
                </h3>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Choose a competitor from the list to view or generate a battlecard
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
